import type { FavoriteView, FavoriteViewFolder } from '@/api/favorites'
import { VpIcon, VpIconButton } from '@vtmn-play/react'
import { VpLoader } from '@vtmn-play/react/headless'
import { Trans, useTranslation } from 'react-i18next'
import { FavoriteViewCard } from '@/components/FavoriteViews/FavoriteViewCard'
import { CHECK_EMOJI_UNIFIED, FavoriteViewEmoji } from '@/components/FavoriteViews/FavoriteViewEmoji'
import { FavoriteViewEmojiRenderer } from '@/components/FavoriteViews/FavoriteViewEmojiRenderer'
import { ALL_SAVED_VIEWS_ID } from '@/components/FavoriteViews/FavoriteViewFolderCard'
import { NoResultsIllustration } from '@/components/NoResultsIllustration'
import { useFavoriteViewsPageActionsContext } from './useFavoriteViewsPageActionsContext'
import './favorite-views-folder-detail.scss'

interface FavoriteViewsFolderDetailProps {
  isLoading: boolean
  selectedFolder?: FavoriteViewFolder
  views: FavoriteView[]
  getCountryLabel: (view: FavoriteView) => string
}

export function FavoriteViewsFolderDetail({
  isLoading,
  selectedFolder,
  views,
  getCountryLabel,
}: Readonly<FavoriteViewsFolderDetailProps>) {
  const { t } = useTranslation()
  const {
    onBack,
    onOpenView,
    onRenameView,
    onDeleteView,
    onShareView,
    onRenameFolder,
    onDeleteFolder,
  } = useFavoriteViewsPageActionsContext()

  if (isLoading)
    return <div className="favorite-views-page__loading"><VpLoader size="large" /></div>

  if (!selectedFolder)
    return <div className="favorite-views-page__empty" data-testid="favorite-views-missing-folder"><h3>{t('favorite-views.empty.no-views-title')}</h3></div>

  const isAllViewsSelected = selectedFolder.id === ALL_SAVED_VIEWS_ID

  return (
    <div className="favorite-views-page__folder-detail">
      <nav className="favorite-views-page__breadcrumbs" aria-label={t('favorite-views.page.breadcrumb')}>
        <ol>
          <li><button type="button" onClick={onBack}>{t('favorite-views.page.title')}</button></li>
          <li aria-current="page">{selectedFolder.name}</li>
        </ol>
      </nav>
      <header className="favorite-views-page__folder-detail-heading">
        <div className="favorite-views-page__folder-detail-title">
          <span className={`favorite-views-page__folder-detail-emoji${isAllViewsSelected ? ' favorite-views-page__folder-detail-emoji--all' : ''}`}>
            {isAllViewsSelected
              ? <FavoriteViewEmoji unified={CHECK_EMOJI_UNIFIED} />
              : <FavoriteViewEmojiRenderer unified={selectedFolder.emoji} size={28} />}
          </span>
          <h2>{selectedFolder.name}</h2>
          <span>{t('favorite-views.page.favorites-count', { count: selectedFolder.viewCount })}</span>
        </div>
        {!isAllViewsSelected && (
          <div className="favorite-views-page__folder-detail-actions">
            <VpIconButton
              type="button"
              variant="tertiary"
              size="medium"
              aria-label={t('favorite-views.management.rename-folder-named', { name: selectedFolder.name })}
              onClick={() => onRenameFolder(selectedFolder)}
              data-testid={`favorite-views-rename-folder-${selectedFolder.id}`}
            >
              <VpIcon name="edit" />
            </VpIconButton>
            <VpIconButton
              type="button"
              variant="tertiary"
              size="medium"
              aria-label={t('favorite-views.management.delete-folder-named', { name: selectedFolder.name })}
              onClick={() => onDeleteFolder(selectedFolder)}
              data-testid={`favorite-views-delete-folder-${selectedFolder.id}`}
            >
              <VpIcon name="delete-bin" />
            </VpIconButton>
          </div>
        )}
      </header>
      {views.length === 0
        ? (
            <div className="favorite-views-page__empty" data-testid={isAllViewsSelected ? 'favorite-views-no-favorites' : 'favorite-views-empty-folder'}>
              <NoResultsIllustration />
              <h3>{t(isAllViewsSelected ? 'favorite-views.empty.no-favorites-title' : 'favorite-views.empty.no-views-title')}</h3>
              {isAllViewsSelected && (
                <p>
                  <Trans
                    i18nKey="favorite-views.empty.no-favorites-description"
                    components={{ favoriteIcon: <VpIcon name="heart" /> }}
                  />
                </p>
              )}
            </div>
          )
        : (
            <ul className="favorite-views-page__view-grid">
              {views.map(view => (
                <FavoriteViewCard
                  key={view.id}
                  view={view}
                  countryLabel={getCountryLabel(view)}
                  onOpen={() => onOpenView(view)}
                  onRename={title => onRenameView(view, title)}
                  onDelete={() => onDeleteView(view)}
                  onShare={() => onShareView(view)}
                />
              ))}
            </ul>
          )}
    </div>
  )
}
